/**
 * TanStack Query wiring: one client, one place that owns cache keys.
 *
 * Keys are arrays that always start with the resource name so a mutation can
 * invalidate a whole family (`['approvals']`) without listing every filter.
 */

import { QueryClient } from '@tanstack/react-query';
import { ApiError } from './api';

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: true,
        retry(failureCount, error) {
          // Auth and not-found never fix themselves; everything else gets two goes.
          if (error instanceof ApiError && (error.isAuth || error.isNotFound)) return false;
          return failureCount < 2;
        },
      },
      mutations: {
        retry: false,
      },
    },
  });
}

export const queryKeys = {
  me: () => ['me'] as const,
  agents: () => ['agents'] as const,
  overview: (tenantId: string) => ['overview', tenantId] as const,
  runs: (filters?: Record<string, unknown>) => ['runs', filters ?? {}] as const,
  run: (runId: string) => ['run', runId] as const,
  runEvents: (runId: string) => ['run-events', runId] as const,
  sessions: (filters?: Record<string, unknown>) => ['sessions', filters ?? {}] as const,
  sessionMessages: (sessionId: string) => ['session-messages', sessionId] as const,
  currentSession: (agentId: string) => ['sessions', 'current', agentId] as const,
  approvals: (status: 'pending' | 'history') => ['approvals', status] as const,
  skills: (filters?: Record<string, unknown>) => ['skills', filters ?? {}] as const,
  memories: (filters?: Record<string, unknown>) => ['memories', filters ?? {}] as const,
  integrations: () => ['integrations'] as const,
  connectors: () => ['connectors'] as const,
  audit: (filters?: Record<string, unknown>) => ['audit', filters ?? {}] as const,
  people: () => ['people'] as const,
  usage: (filters?: Record<string, unknown>) => ['usage', filters ?? {}] as const,
  utilisation: (filters?: Record<string, unknown>) => ['utilisation', filters ?? {}] as const,
  artifacts: (filters?: Record<string, unknown>) => ['artifacts', filters ?? {}] as const,
  schedules: (filters?: Record<string, unknown>) => ['schedules', filters ?? {}] as const,
  triggers: (filters?: Record<string, unknown>) => ['triggers', filters ?? {}] as const,
  holds: (filters?: Record<string, unknown>) => ['holds', filters ?? {}] as const,
  rules: (filters?: Record<string, unknown>) => ['rules', filters ?? {}] as const,
  agentsArchived: () => ['agents', 'archived'] as const,
  master: {
    fleet: () => ['master', 'fleet'] as const,
    orgs: () => ['master', 'orgs'] as const,
    orgAgents: (tenantId: string) => ['master', 'orgs', tenantId, 'agents'] as const,
    orgPeople: (tenantId: string) => ['master', 'orgs', tenantId, 'people'] as const,
    incidents: (filters?: Record<string, unknown>) =>
      ['master', 'incidents', filters ?? {}] as const,
    releases: () => ['master', 'releases'] as const,
    billing: (filters?: Record<string, unknown>) => ['master', 'billing', filters ?? {}] as const,
  },
} as const;

/** The message a screen shows when a query fails. Never leaks a stack. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 0) return 'Could not reach the Otto IQ API. Check your connection.';
    if (error.status === 401) return 'Your session has expired. Sign in again to continue.';
    if (error.status === 403) return 'You do not have access to this.';
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}
